import { API_URL } from '../config';

const ACCESS_TOKEN_KEY = 'adminToken';
const REFRESH_TOKEN_KEY = 'adminRefreshToken';

export const authService = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  refreshAccessToken: async (): Promise<string | null> => {
    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        authService.clearTokens();
        return null;
      }

      const data = await res.json();
      authService.setTokens(data.token, data.refreshToken);
      return data.token;
    } catch {
      authService.clearTokens();
      return null;
    }
  },

  // Wrapper for authenticated API calls with automatic token refresh
  authFetch: async (url: string, options: RequestInit = {}): Promise<Response> => {
    let token = authService.getAccessToken();

    const makeRequest = async (authToken: string | null) => {
      const headers = new Headers(options.headers);
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`);
      }
      return fetch(url, { ...options, headers });
    };

    let response = await makeRequest(token);

    // If unauthorized, try to refresh token and retry
    if (response.status === 401) {
      const data = await response.clone().json().catch(() => ({}));

      // Check if token expired (vs invalid credentials)
      if (data.code === 'TOKEN_EXPIRED' || data.message === 'Token has been invalidated') {
        const newToken = await authService.refreshAccessToken();
        if (newToken) {
          response = await makeRequest(newToken);
        }
      }
    }

    return response;
  },
};

export default authService;
